import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Star, ArrowRight, BarChart3, ClipboardList } from "lucide-react";

interface ReviewsFeatureAnnouncementProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  basePath: string;
}

const STEPS = [
  {
    title: "Conheça as avaliações",
    description: "Veja o que seus participantes acharam do evento.",
  },
  {
    title: "Adapte às suas necessidades",
    description: "Crie perguntas que importam para o seu evento.",
  },
  {
    title: "Transforme em resultados",
    description: "Decisões melhores com dados reais do público.",
  },
] as const;

function StarsAnimation() {
  return (
    <div className="flex items-center justify-center gap-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0, rotate: -30 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{
            delay: i * 0.1,
            duration: 0.4,
            type: "spring",
            stiffness: 260,
            damping: 15,
          }}
        >
          <Star className="h-10 w-10 fill-amber-400 text-amber-400 drop-shadow-sm" />
        </motion.div>
      ))}
    </div>
  );
}

function SurveyAnimation() {
  const lines = [90, 70, 85, 60];
  return (
    <div className="flex justify-center">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="rounded-2xl border border-border/60 bg-card p-5 w-52 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <ClipboardList className="h-4 w-4 text-primary" />
          </div>
          <div className="h-2.5 w-16 rounded-full bg-muted" />
        </div>
        {lines.map((w, i) => (
          <div key={i} className="flex items-center gap-2.5 mb-3 last:mb-0">
            <div className="h-3.5 w-3.5 rounded-full border-2 border-primary/30 shrink-0" />
            <div className="h-3 rounded-full bg-muted/60 overflow-hidden flex-1">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-primary/40 to-primary/25"
                initial={{ width: 0 }}
                animate={{ width: `${w}%` }}
                transition={{ delay: 0.3 + i * 0.12, duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function ChartAnimation() {
  const bars = [40, 65, 55, 80, 95];
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-end gap-2.5 h-28">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            className="w-9 rounded-t-lg bg-gradient-to-t from-primary/70 to-primary/50"
            initial={{ height: 0 }}
            animate={{ height: `${h}%` }}
            transition={{ delay: 0.2 + i * 0.08, duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
          />
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.7, duration: 0.3 }}
        className="flex items-baseline gap-1.5"
      >
        <span className="font-display text-4xl font-bold text-foreground">4.8</span>
        <span className="text-base text-muted-foreground font-medium">/ 5</span>
      </motion.div>
    </div>
  );
}

const VISUALS = [StarsAnimation, SurveyAnimation, ChartAnimation];

const EASE = [0.23, 1, 0.32, 1] as const;

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0,
  }),
};

export function ReviewsFeatureAnnouncement({
  open,
  onOpenChange,
  eventId,
  basePath,
}: ReviewsFeatureAnnouncementProps) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const navigate = useNavigate();

  const goNext = useCallback(() => {
    if (step < 2) {
      setDirection(1);
      setStep((s) => s + 1);
    }
  }, [step]);

  const handleSkip = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleCTA = useCallback(() => {
    onOpenChange(false);
    navigate(`${basePath}/avaliacoes`);
  }, [onOpenChange, navigate, basePath]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setDirection(1);
    }
  }, [open]);

  const Visual = VISUALS[step];
  const isLast = step === 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[400px] w-full p-0 gap-0 overflow-hidden border-0 shadow-2xl rounded-3xl"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Header gradient area */}
        <div className="relative bg-gradient-to-b from-primary/8 via-primary/4 to-transparent px-8 pt-8 pb-6">
          {/* Grip indicator */}
          <div className="flex justify-center mb-6">
            <div className="h-1 w-10 rounded-full bg-foreground/10" />
          </div>

          {/* Visual content area - fixed height */}
          <div className="relative overflow-hidden h-[160px] flex items-center justify-center">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={step}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: EASE }}
                className="w-full"
              >
                <Visual />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Text content */}
        <div className="px-8 pb-2 text-center">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={`text-${step}`}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: EASE, delay: 0.05 }}
            >
              <h2 className="font-display text-xl font-bold text-foreground mb-2 tracking-tight">
                {STEPS[step].title}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {STEPS[step].description}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-8 pb-8 pt-6">
          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="h-1.5 rounded-full"
                animate={{
                  width: i === step ? 28 : 8,
                  backgroundColor:
                    i === step
                      ? "hsl(var(--primary))"
                      : "hsl(var(--muted-foreground) / 0.15)",
                }}
                transition={{ duration: 0.3, ease: EASE }}
              />
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="flex-1 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 h-11 font-medium"
            >
              Pular
            </Button>
            {isLast ? (
              <Button
                onClick={handleCTA}
                className="flex-1 rounded-xl font-semibold gap-2 h-11 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md shadow-primary/20"
              >
                Ir para avaliações
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={goNext}
                className="flex-1 rounded-xl font-semibold h-11 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md shadow-primary/20"
              >
                Entender mais
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useReviewsAnnouncement(eventId: string | undefined) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    if (window.matchMedia("(max-width: 767px)").matches) return;
    const key = `reviews-announcement-seen:${eventId}`;
    const seen = localStorage.getItem(key);
    if (!seen) setShow(true);
  }, [eventId]);

  const dismiss = useCallback(() => {
    if (!eventId) return;
    localStorage.setItem(`reviews-announcement-seen:${eventId}`, "1");
    setShow(false);
  }, [eventId]);

  return { show, dismiss };
}
